import { isDifferential, isPowerOperator } from "@corca/core";

import {
  CONSTANT_TO_SYMPY_MAP,
  FUNCTION_KEY_TO_SYMPY_MAP,
  OPERATOR_KEY_TO_SYMPY_MAP,
  SET_SYMBOL_TO_SYMPY_MAP,
} from "../mappings";
import { unitInlineChildrenToSemantic } from "../unitToSympyMap";

import { pairDenominatorDifferentials } from "./parseDerivative";
import {
  constantExpr,
  fn,
  numberExpr,
  op,
  operatorSymbolString,
  getLabeledVariableName,
  getNumberScalarRatioDivisor,
  SympyBuildError,
} from "./shared";
import { TRIG_IDENTITY_BUILDERS } from "./trigIdentities";

import type { SympyBuilder } from "./SympyBuilder";
import type { SemanticExpr } from "../types";
import type { NodeJson } from "@corca/data";

/** Append `Pow` if the node has a direct power-operator child (child pattern, e.g. 2^3). */
export function appendChildPower(
  builder: SympyBuilder,
  expr: SemanticExpr,
  node: NodeJson,
  level: number,
): SemanticExpr {
  const power = node.children.find(isPowerOperator);
  if (power) {
    const exponent = builder.traverseNode(power.children[0], level + 1);
    // An empty exponent renders as nothing, so `x^` is just `x`.
    if (exponent !== null) return op("Pow", [expr, exponent]);
  }
  return expr;
}

/* -------------------------------------------------------------------------- */
/* Core node → SemanticExpr                                                   */
/* -------------------------------------------------------------------------- */

/** Node → expression, or null when the node renders to nothing (an empty inline, group or cell). */
export function traverseNode(
  builder: SympyBuilder,
  node: NodeJson,
  level: number,
): SemanticExpr | null {
  if (node.type === "wrapper") {
    // A wrapper holds the line's single inline; fold defensively in case it ever holds more.
    return node.children.length === 1
      ? builder.traverseNode(node.children[0], level + 1)
      : builder.foldInlineChildren(node.children, level);
  }

  if (
    (node.type === "variable" || node.type === "constant") &&
    node.children[0].type === "symbol"
  ) {
    const symbolKey = node.children[0].props.key;
    if (node.type === "constant") {
      if (symbolKey && symbolKey in CONSTANT_TO_SYMPY_MAP) {
        return constantExpr(CONSTANT_TO_SYMPY_MAP[symbolKey]);
      }
    }

    // Blackboard-bold set symbols (ℝ ℤ ℕ ℚ ℂ) are variable nodes but denote
    // SymPy's predefined sets, not free symbols.
    if (node.type === "variable" && symbolKey && symbolKey in SET_SYMBOL_TO_SYMPY_MAP) {
      return op(SET_SYMBOL_TO_SYMPY_MAP[symbolKey], []);
    }

    if (node.type === "variable" && builder.boundVarsScope.size > 0) {
      const indexed = builder.tryEmitIndexedAccess(node);
      if (indexed) return indexed;
    }

    const varName = getLabeledVariableName(node) ?? "";

    if (!builder.variables.has(varName)) {
      const varSymbol =
        node.type === "constant"
          ? `const${builder.variables.size}`
          : `var${builder.variables.size}`;
      builder.variables.set(varName, varSymbol);
    }

    if (!builder.symbolNodes.has(varName)) {
      const symbolChild = node.children[0];
      builder.symbolNodes.set(varName, {
        type: node.type,
        props: node.props,
        children: [symbolChild],
      });
    }

    const symbolExpr: SemanticExpr = { type: "symbol", name: varName };

    const inlineChildren = node.children.filter((n) => n.type === "inline");
    if (node.type === "variable" && inlineChildren.length > 0) {
      const args = inlineChildren.map((n) => builder.traverseNode(n, level + 1)).filter(Boolean);
      return op("Call", [symbolExpr, ...args]);
    }

    return symbolExpr;
  }

  if (node.type === "inline") {
    return builder.foldInlineNode(node, level);
  }

  if (node.type === "operatorSymbol") {
    throw new SympyBuildError(`Unsupported operator: ${operatorSymbolString(node)}`);
  }

  if (node.type === "function") {
    const { key = "" } = node.props;

    if (key === "function-limit") {
      return builder.parseLimit(node);
    }
    if (key === "function-summation") {
      return builder.parseSumProduct(node, "Sum");
    }
    if (key === "function-product") {
      return builder.parseSumProduct(node, "Product");
    }

    const funcSympy =
      key in FUNCTION_KEY_TO_SYMPY_MAP
        ? FUNCTION_KEY_TO_SYMPY_MAP[key as keyof typeof FUNCTION_KEY_TO_SYMPY_MAP]
        : null;
    const trigIdentity = TRIG_IDENTITY_BUILDERS[key];
    let text: SemanticExpr | null = null;
    if (node.props.key === "function-logarithm") {
      text = builder.parseLogarithm(node, level);
    } else if (node.props.key === "function-lb") {
      text = builder.parseLogarithm(node, level, 2);
    } else if (trigIdentity) {
      const arg = node.children
        .filter((n) => n.type === "inline")
        .map((n) => builder.traverseNode(n, level + 1))
        .filter(Boolean);
      // Unary identity (versine, haversine, …) expanded to its structured sin/cos definition.
      text = trigIdentity(arg[0]);
    } else if (funcSympy) {
      const args = node.children
        .filter((n) => n.type === "inline")
        .map((n) => builder.traverseNode(n, level + 1))
        .filter(Boolean);
      text = fn(funcSympy, args);
    }
    // No SymPy mapping for this function, so there is nothing to build from it.
    if (text === null) return null;
    return appendChildPower(builder, text, node, level);
  }

  if (node.type === "number") {
    const value = node.props.value.toString();
    let text: SemanticExpr;

    const scalarRatioDivisor = getNumberScalarRatioDivisor(node);
    if (scalarRatioDivisor !== null) {
      // Scalar ratio (%, ‰, ‱) is dimensionless: emit value/divisor.
      text = op("Frac", [numberExpr(value), numberExpr(scalarRatioDivisor.toString())]);
    } else {
      text = numberExpr(value);
      if (builder.emitSiUnits) {
        const unitChild = node.children.find((c) => c.type === "unit");
        if (unitChild) {
          const inlineChild = unitChild.children.find((c) => c.type === "inline");
          if (inlineChild) {
            const unitFactors = unitInlineChildrenToSemantic(inlineChild.children);
            if (unitFactors) {
              // Structured `Mul(number, ...unitFactors)` matching SymPy's serialized unit product.
              text = op("Mul", [numberExpr(value), ...unitFactors]);
            }
          }
        }
      }
    }

    return appendChildPower(builder, text, node, level);
  }

  if (node.type === "operator") {
    switch (node.props.operatorType) {
      case "power":
        throw new SympyBuildError("Power operator is missing a base");
      case "fraction": {
        const numerator = node.children[0];
        const denominator = node.children[1];

        if (
          numerator.type === "inline" &&
          numerator.children.length === 1 &&
          numerator.children[0].type === "function" &&
          isDifferential(numerator.children[0]) &&
          denominator.type === "inline" &&
          denominator.children.length > 0
        ) {
          const denominatorPairs = pairDenominatorDifferentials(denominator.children);
          if (denominatorPairs) {
            const result = builder.parseFractionDerivative(numerator.children[0], denominatorPairs);
            if (result) return result;
          }
        }

        const numExpr = builder.traverseNode(numerator, level + 1);
        const denExpr = builder.traverseNode(denominator, level + 1);
        if (numExpr === null || denExpr === null) {
          throw new SympyBuildError("Fraction is missing a numerator or denominator");
        }
        return op("Frac", [numExpr, denExpr]);
      }
      case "root": {
        const indexExpr = builder.traverseNode(node.children[0], level + 1);
        const radicand = builder.traverseNode(node.children[1], level + 1);
        if (radicand === null) throw new SympyBuildError("Root is missing a radicand");
        // Root takes the radicand first, the reverse of the node's children; a bare √ omits the
        // index, which normalizeSemantic defaults to 2.
        return indexExpr === null ? op("Root", [radicand]) : op("Root", [radicand, indexExpr]);
      }
      default: {
        const opSympy = OPERATOR_KEY_TO_SYMPY_MAP[node.props.key ?? ""];
        if (opSympy) {
          // opSympy is a fully-qualified name (e.g. "sp.factorial").
          return fn(
            opSympy,
            node.children.map((n) => builder.traverseNode(n, level + 1)).filter(Boolean),
          );
        }
        throw new SympyBuildError(`Unsupported operator type: ${node.props.operatorType}`);
      }
    }
  }

  if (node.type === "group") {
    const opening = node.props.openingSymbol;
    const closing = node.props.closingSymbol;
    // A group normally holds one inline; a bare child list still goes through the inline fold so its
    // operator symbols are interpreted.
    const content =
      node.children.length === 1
        ? builder.traverseNode(node.children[0], level + 1)
        : builder.foldInlineChildren(node.children, level);

    // An empty group renders to nothing, brackets included.
    if (content === null) return null;

    if (opening === "⌊" && closing === "⌋") {
      return fn("floor", [content]);
    }
    if (opening === "⌈" && closing === "⌉") {
      return fn("ceiling", [content]);
    }
    if ((opening === "|" && closing === "|") || (opening === "‖" && closing === "‖")) {
      return fn("Abs", [content]);
    }

    // Plain parentheses carry no meaning of their own: the folded tree already encodes the grouping.
    return content;
  }

  if (node.type === "matrix") {
    const rows = node.props.rows || 1;
    const cols = node.props.cols || 1;
    const cellExprs = node.children.map((n) => builder.traverseNode(n, level + 1));

    if (rows * cols <= 1) {
      // A 1x1 matrix is a parenthesized scalar expression, not a real matrix, so unwrap the cell
      // rather than building a matrix an enclosing Pow could not exponentiate.
      return cellExprs.length === 1 ? cellExprs[0] : op("MatrixScalar", cellExprs.filter(Boolean));
    }

    // A real matrix needs every cell: an unfilled one has no value to compute with.
    if (cellExprs.some((cell) => cell === null)) {
      throw new SympyBuildError("Matrix has an empty cell");
    }

    return { type: "matrix", rows, cols, data: cellExprs.filter(Boolean) };
  }

  if (node.type === "plainText") {
    return null;
  }

  throw new SympyBuildError(`Unknown node type: ${node.type}`);
}
